import axios from 'axios';
import type { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ETokenType } from 'src/types';
import { DemoMarket, demoEngine } from './engine';
import { getChainId, getCurrentBlockNum } from './blockChain';
import { RATE_CONFIG } from './rateBands';

// Captured at module load, before common.ts replaces the default adapter.
// Used to pass through non-API requests (e.g. /data/glossary.json) to the
// real network so static files are served normally.
const originalAdapter = axios.defaults.adapter as AxiosAdapter;

function ok<T>(data: T, config: AxiosRequestConfig): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
    request: {} as any,
  };
}

function apiOk(payload: any, config: AxiosRequestConfig) {
  // Real backend omits errorCode/errorMsgKey on success; handleError()
  // treats any `errorCode !== undefined` as a failure, so emitting 0 here
  // would make every successful response throw.
  return ok({ success: true, data: payload }, config);
}

function apiFail(errorMsgKey: string, config: AxiosRequestConfig, status = 401) {
  return ok({ success: false, errorCode: 1001, errorMsgKey, data: { reason: errorMsgKey } }, { ...config });
}

function stripBase(url: string): string {
  const i = url.indexOf('/api/');
  return i >= 0 ? url.slice(i) : url;
}

function parseQuery(url: string, config: AxiosRequestConfig): Record<string, string> {
  // axios 0.24 leaves config.params separate from config.url when a custom
  // adapter is in use (buildURL happens inside the default xhr/http adapter),
  // so we must merge both.
  const out: Record<string, string> = {};
  const q = url.indexOf('?');
  if (q >= 0) {
    const sp = new URLSearchParams(url.slice(q + 1));
    sp.forEach((v, k) => { out[k] = v; });
  }
  const params = config.params;
  if (params && typeof params === 'object') {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      if (typeof v === 'string') out[k] = v;
      else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
      else out[k] = JSON.stringify(v);
    }
  }
  return out;
}

function tokensPayload() {
  const tokens = demoEngine.getTokens().map(t => ({
    tokenId: t.tokenId,
    code: t.code,
    name: t.name,
    tokenType: ETokenType.ERC20,
    valueType: 0,
    tokenValuationProtocol: 0,
    tokenAddress: `0xdemo${t.tokenId.toString().padStart(40, '0')}`.slice(0, 42),
    decimals: t.decimals,
    nativeToken: t.code === 'ETH',
    withdrawFee: '0',
    price: t.price,
  }));
  return { tokens };
}

function baseMarketRow(m: DemoMarket) {
  const ms = demoEngine.getMarketState(m.marketId);
  const cfg = RATE_CONFIG[m.code];
  const midRate = ms?.midRate ?? (cfg.floating.lendLow + cfg.floating.borrowHigh) / 2;
  const lendDeposits = ms?.lendDepositsTokens ?? 0;
  const borrowDeposits = ms?.borrowDepositsTokens ?? 0;
  const base: any = {
    marketId: m.marketId,
    tokenId: m.tokenId,
    quantityStep: '0.000001',
    minQuantity: '1',
    maxQuantity: '100000000',
    rateStep: '0.0001',
    category: m.isFloating ? 1 : 2,
    enable: true,
    name: m.name,
    instrumentId: m.instrumentId,
    direction: true,
    rate: midRate.toFixed(6),
    midRate: midRate.toFixed(6),
    updated: true,
    updateDate: Date.now(),
    deposits: Math.round(lendDeposits),
    borrows: Math.round(borrowDeposits),
    subscriptions: (lendDeposits + borrowDeposits).toFixed(2),
    volume24: (ms?.volume24Usd ?? 0).toFixed(2),
    rate24: midRate.toFixed(6),
    takerFeeRate: '0.0001',
    makerFeeRate: '0.00005',
    interestFeeRate: '0.001',
    borrowLpFeeRate: '0.0002',
    lendLpFeeRate: '0.0002',
    borrowPriceIndex: '1',
    lendPriceIndex: '1',
    priceIndexDate: Date.now(),
    actualRate: midRate.toFixed(6),
    referenceMarketSize: (lendDeposits + borrowDeposits).toFixed(2),
  };
  if (!m.isFloating) {
    base.maturityDate = m.maturityDate;
    base.daysToMaturity = m.daysToMaturity;
  }
  return base;
}

function marketsAllPayload() {
  const markets = demoEngine.getMarkets().map(baseMarketRow);
  return { markets };
}

function orderBookPayload(marketId: number, limit: number) {
  const ms = demoEngine.getMarketState(marketId);
  if (!ms) return { asks: [], bids: [] };
  const lim = Math.max(1, Math.min(limit, 20));
  return {
    asks: ms.asks.slice(0, lim).map(e => ({ rate: e.rate, quantity: e.quantity })),
    bids: ms.bids.slice(0, lim).map(e => ({ rate: e.rate, quantity: e.quantity })),
  };
}

function recentTradesPayload(marketId: number, limit: number) {
  const ms = demoEngine.getMarketState(marketId);
  const trxs = (ms?.recentTrades ?? []).slice(0, limit).map(t => ({
    side: t.s,
    rate: t.p,
    quantity: t.q,
    date: t.d,
  }));
  return { trxs };
}

function marketsDashboardPayload(tokenId: number) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId);
  const float = markets.find(m => m.isFloating);
  const fixed = markets.filter(m => !m.isFloating);
  const toData = (m: DemoMarket) => {
    const ms = demoEngine.getMarketState(m.marketId);
    if (!ms) return null;
    return {
      marketId: m.marketId,
      side: 0,
      bid: ms.bids[0]?.rate ?? '0',
      bidStepSize: ms.bids[0]?.quantity ?? '0',
      ask: ms.asks[0]?.rate ?? '0',
      askStepSize: ms.asks[0]?.quantity ?? '0',
      dv01: '0',
      position: '0',
      carry: '0',
    };
  };
  const frData = fixed.map(m => {
    const base = toData(m);
    if (!base) return null;
    return { ...base, daysToMaturity: m.daysToMaturity!, marketRate: base.bid, pv01Series: '0' };
  }).filter(Boolean);
  return { ir: float ? toData(float) : null, fr: frData };
}

function marketsPastRatesPayload(tokenId: number) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId);
  const rows = markets.map(m => {
    const ms = demoEngine.getMarketState(m.marketId);
    const rate = ms?.midRate ?? 0;
    const rate24 = rate * (1 + (Math.random() - 0.5) * 0.02);
    return {
      tokenId: m.tokenId,
      marketId: m.marketId,
      daysToMaturity: m.daysToMaturity,
      maturityDate: m.maturityDate,
      date: Date.now(),
      open: rate24.toFixed(6),
      close: rate.toFixed(6),
      high: (rate * 1.01).toFixed(6),
      low: (rate * 0.99).toFixed(6),
      volume: (ms?.volume24Usd ?? 0).toFixed(2),
      totalValue: (ms?.volume24Usd ?? 0).toFixed(2),
      lendDepth: (ms?.lendDepositsTokens ?? 0).toFixed(2),
      borrowDepth: (ms?.borrowDepositsTokens ?? 0).toFixed(2),
      numDaysAgo: 0,
      volume24: (ms?.volume24Usd ?? 0).toFixed(2),
      midRate: rate.toFixed(6),
      midRate24: rate24.toFixed(6),
      midRateDelta24: (rate - rate24).toFixed(6),
      rate: rate.toFixed(6),
      rate24: rate24.toFixed(6),
      rateDelta24: (rate - rate24).toFixed(6),
    };
  });
  return { [tokenId]: rows };
}

function historicalRatesPayload(tokenId: number, days = 30) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId);
  const floatMarket = markets.find(m => m.isFloating);
  const floatingBars = historicalBars(floatMarket?.marketId, days);
  const fixedMap: Record<number, any[]> = {};
  for (const m of markets) {
    if (m.isFloating || !m.daysToMaturity) continue;
    fixedMap[m.daysToMaturity] = historicalBars(m.marketId, days);
  }
  return { floating: floatingBars, fixed: fixedMap };
}

function historicalBars(marketId: number | undefined, days: number) {
  if (marketId === undefined) return [];
  const ms = demoEngine.getMarketState(marketId);
  const baseRate = ms?.midRate ?? 0.05;
  const bars: any[] = [];
  const now = Date.now();
  for (let i = days; i >= 0; i--) {
    const drift = (Math.random() - 0.5) * 0.003;
    const r = Math.max(0.0001, baseRate + drift);
    bars.push({
      date: now - i * 86_400_000,
      open: r.toFixed(6),
      close: r.toFixed(6),
      high: (r * 1.02).toFixed(6),
      low: (r * 0.98).toFixed(6),
      volume: ((ms?.volume24Usd ?? 1_000_000) * (0.7 + Math.random() * 0.6)).toFixed(2),
      totalValue: ((ms?.volume24Usd ?? 1_000_000) * 10).toFixed(2),
      lendDepth: (ms?.lendDepositsTokens ?? 0).toFixed(2),
      borrowDepth: (ms?.borrowDepositsTokens ?? 0).toFixed(2),
      numDaysAgo: i,
    });
  }
  return bars;
}

function allOrdersPayload(limit: number) {
  const markets = demoEngine.getMarkets();
  const orders: any[] = [];
  for (let i = 0; i < limit && i < 200; i++) {
    const m = markets[Math.floor(Math.random() * markets.length)];
    const ms = demoEngine.getMarketState(m.marketId);
    if (!ms) continue;
    const t = ms.recentTrades[Math.floor(Math.random() * ms.recentTrades.length)];
    if (!t) continue;
    orders.push({
      orderId: demoEngine.nextOrderId(),
      marketType: m.isFloating ? 1 : 2,
      marketId: m.marketId,
      instrumentId: m.instrumentId,
      accountId: 1000 + Math.floor(Math.random() * 9999),
      side: t.s,
      orderType: 1,
      quantity: t.q,
      fulfilled: t.q,
      status: 10,
      clientOrderId: `demo-${i}`,
      orderDate: t.d,
      updateDate: t.d,
      source: 1,
      averageTradeRate: t.p,
      rate: t.p,
      maturityDate: m.maturityDate,
      dv01: '0',
      mtm: '0',
      userAddress: '0x' + Math.random().toString(16).slice(2, 10).padStart(40, '0'),
    });
  }
  return { orders, hasNextPage: false };
}

function totalValueHistoricalPayload(limit = 30) {
  const now = Date.now();
  const data: any[] = [];
  let tvl = 150_000_000;
  for (let i = limit; i >= 0; i--) {
    tvl *= 1 + (Math.random() - 0.48) * 0.01;
    data.push({ totalValue: tvl.toFixed(2), date: Math.floor((now - i * 86_400_000) / 1000) });
  }
  return { data };
}

function totalValueLockedHistoricalPayload() {
  const now = Date.now();
  const items: any[] = [];
  let tvl = 150_000_000;
  for (let i = 30; i >= 0; i--) {
    tvl *= 1 + (Math.random() - 0.48) * 0.01;
    items.push({ totalValueLockedUsd: tvl.toFixed(2), date: Math.floor((now - i * 86_400_000) / 1000) });
  }
  return { items };
}

function fixedRateMarketsSizePayload() {
  const markets = demoEngine.getMarkets().filter(m => !m.isFloating);
  return {
    markets: markets.map(m => {
      const ms = demoEngine.getMarketState(m.marketId);
      return {
        tokenId: m.tokenId,
        instrumentId: m.instrumentId,
        name: m.name,
        updated: true,
        deposits: (ms?.lendDepositsTokens ?? 0).toFixed(2),
        borrows: (ms?.borrowDepositsTokens ?? 0).toFixed(2),
        subscriptions: ((ms?.lendDepositsTokens ?? 0) + (ms?.borrowDepositsTokens ?? 0)).toFixed(2),
      };
    }),
  };
}

function yieldCurvePayload(tokenId: number) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId && !m.isFloating);
  const interpolatedRates = markets.map(m => {
    const ms = demoEngine.getMarketState(m.marketId);
    return {
      marketId: m.marketId,
      tokenId: m.tokenId,
      interpolatedPx: (ms?.midRate ?? 0.05).toFixed(6),
      pv01Series: '0',
      daysToMaturity: m.daysToMaturity ?? 0,
      maturityDate: m.maturityDate,
      enable: true,
    };
  });
  return { interpolatedRates };
}

function impliedForwardRatePayload(tokenId: number) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId && !m.isFloating);
  const rates: any[] = [];
  for (let i = 0; i < markets.length - 1; i++) {
    const from = markets[i];
    const to = markets[i + 1];
    const fromMs = demoEngine.getMarketState(from.marketId);
    const toMs = demoEngine.getMarketState(to.marketId);
    rates.push({
      fromMarketId: from.marketId,
      toMarketId: to.marketId,
      fromMaturityDate: from.maturityDate,
      toMaturityDate: to.maturityDate,
      fromRate: (fromMs?.midRate ?? 0.05).toFixed(6),
      toRate: (toMs?.midRate ?? 0.05).toFixed(6),
      daysBetweenNowAndM1: from.daysToMaturity ?? 0,
      daysBetweenNowAndM2: to.daysToMaturity ?? 0,
      daysBetweenM1AndM2: (to.daysToMaturity ?? 0) - (from.daysToMaturity ?? 0),
      impliedForwardRate: (((toMs?.midRate ?? 0.05) + (fromMs?.midRate ?? 0.05)) / 2).toFixed(6),
    });
  }
  return { impliedForwardRates: rates };
}

function yieldCurveHistoricalPayload(tokenId: number, days = 28) {
  const markets = demoEngine.getMarkets().filter(m => m.tokenId === tokenId && !m.isFloating);
  const out: Record<string, any[]> = {};
  const now = Date.now();
  // Chart parses keys via `moment(genDateStr)` so ISO YYYY-MM-DD is required;
  // epoch-seconds strings would resolve to year 1745 and never render.
  for (let d = days; d >= 0; d--) {
    const dt = new Date(now - d * 86_400_000);
    const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    out[key] = markets.map((m, idx) => {
      const ms = demoEngine.getMarketState(m.marketId);
      // Label must be the short rolling-day code (e.g. "1D") to align with the
      // chart's column buttons; the full market name "ETH 1D" would never match.
      return {
        marketId: m.marketId,
        rate: ((ms?.midRate ?? 0.05) * (1 + (Math.random() - 0.5) * 0.02)).toFixed(6),
        label: m.rollingLabel ?? m.name,
        xpos: idx,
      };
    });
  }
  return { historicalCurve: out };
}

function rateBucketsPayload() {
  const markets = demoEngine.getMarkets();
  const rateBuckets = markets.flatMap(m => {
    const ms = demoEngine.getMarketState(m.marketId);
    if (!ms) return [];
    return [0, 1, 2].map(i => ({
      marketId: m.marketId,
      rate: (ms.midRate + i * 0.001).toFixed(6),
      quantity: (ms.lendDepositsTokens / (20 + i * 10)).toFixed(6),
    }));
  });
  return { rateBuckets };
}

function fixedRateMarketByIdPayload(marketId: number) {
  const m = demoEngine.getMarkets().find(mm => mm.marketId === marketId);
  if (!m) return { market: null };
  return { market: baseMarketRow(m) };
}

type Handler = (url: string, query: Record<string, string>, config: AxiosRequestConfig) => any;

function serverTimePayload() {
  const now = Date.now();
  return { clientTime: now, serverTime: now, marketRolloverHour: 0 };
}

function serverStatusPayload() {
  return { jobServerStatus: 1, webServerStatus: 1, tradeServerStatus: 1 };
}

function blockChainPayload() {
  return {
    blockChain: {
      name: 'Hoodi',
      processedBlockNum: getCurrentBlockNum(),
      processedBlockHash: '0x' + '0'.repeat(64),
      chainId: getChainId(),
    },
  };
}

const handlers: Array<[RegExp, Handler]> = [
  [/^\/api\/server\/time/, () => serverTimePayload()],
  [/^\/api\/server\/status/, () => serverStatusPayload()],
  [/^\/api\/p\/blockChain/, () => blockChainPayload()],
  [/^\/api\/p\/tokens(\?.*)?$/, () => tokensPayload()],
  [/^\/api\/p\/token\/(\d+)$/, (u) => {
    const id = Number(u.match(/\/api\/p\/token\/(\d+)/)![1]);
    const t = demoEngine.getTokens().find(tk => tk.tokenId === id);
    return { token: t };
  }],
  [/^\/api\/p\/rate\/markets\/all(\?.*)?$/, () => marketsAllPayload()],
  [/^\/api\/p\/rate\/markets\/dashboard/, (_u, q) => marketsDashboardPayload(Number(q.tokenId))],
  [/^\/api\/p\/rate\/markets\/past\/rates/, (_u, q) => marketsPastRatesPayload(Number(q.tokenId))],
  [/^\/api\/p\/rate\/market\/historicalRates/, (_u, q) => historicalRatesPayload(Number(q.tokenId), Number(q.daysToInclude) || 30)],
  [/^\/api\/p\/(floating_rate|fixed_rate)\/market\/(\d+)\/orderBook/, (u, q) => {
    const mid = Number(u.match(/\/market\/(\d+)\//)![1]);
    return { orderBook: orderBookPayload(mid, Number(q.limit) || 20) };
  }],
  [/^\/api\/p\/(floating_rate|fixed_rate)\/market\/(\d+)\/trxs\/recent/, (u, q) => {
    const mid = Number(u.match(/\/market\/(\d+)\//)![1]);
    return recentTradesPayload(mid, Number(q.limit) || 100);
  }],
  [/^\/api\/p\/rate\/allOrders/, (_u, q) => allOrdersPayload(Number(q.limit) || 50)],
  [/^\/api\/p\/rate\/market\/historical\/totalValueUsd/, (_u, q) => totalValueHistoricalPayload(Number(q.limit) || 30)],
  [/^\/api\/p\/totalValueLocked\/historical/, () => totalValueLockedHistoricalPayload()],
  [/^\/api\/p\/fixed_rate\/markets\/size/, () => fixedRateMarketsSizePayload()],
  [/^\/api\/p\/fixed_rate\/yieldCurve\/estimated/, (_u, q) => yieldCurvePayload(Number(q.tokenId))],
  [/^\/api\/p\/fixed_rate\/yieldCurve\/historical/, (_u, q) => yieldCurveHistoricalPayload(Number(q.tokenId), Number(q.daysToInclude) || 28)],
  [/^\/api\/p\/fixed_rate\/impliedForwardRate/, (_u, q) => impliedForwardRatePayload(Number(q.tokenId))],
  [/^\/api\/p\/fixed_rate\/market\/(\d+)$/, (u) => {
    const mid = Number(u.match(/\/market\/(\d+)$/)![1]);
    return fixedRateMarketByIdPayload(mid);
  }],
  [/^\/api\/p\/rate\/orders\/rateBuckets/, () => rateBucketsPayload()],
];

export const mockAxiosAdapter: AxiosAdapter = async (config) => {
  const fullUrl = config.url ?? '';
  const stripped = stripBase(fullUrl);
  const url = stripped.split('?')[0];

  // Pass non-API requests (static files like /data/glossary.json, and any
  // absolute third-party URL) straight to the real network adapter.
  if (!url.startsWith('/api/')) {
    return originalAdapter(config);
  }

  await demoEngine.boot();
  const query = parseQuery(fullUrl, config);

  // Authenticated user endpoints — demo is view-only, hide user state.
  if (url.startsWith('/api/user/')) {
    return apiFail('error.demo.nouser', config, 200);
  }

  for (const [re, h] of handlers) {
    if (re.test(stripped)) {
      const payload = h(stripped, query, config);
      return apiOk(payload, config);
    }
  }
  // Unknown /api/ path — return empty success so nothing throws.
  return apiOk({}, config);
};
